import {
  forwardRef,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import type {
  ComponentType,
  ForwardedRef,
  ReactElement,
  Ref,
  RefObject,
} from 'react';

import { useComposedRefs } from '@radix-ui/react-compose-refs';
import type { NumberInputProps } from './types';
import {
  ActionButton,
  ButtonDivider,
  StyledNumberInput,
  Wrapper,
} from './styled';

const dispatchChangeEvent = (input: HTMLInputElement, value: string) => {
  const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
    input.constructor.prototype,
    'value',
  )!.set!;
  nativeInputValueSetter.call(input, value);

  const changeEvent = new Event('input', { bubbles: true });
  input.dispatchEvent(changeEvent);
};

function NumberInputInner<
  RefElementType,
  InputComponent extends
  | keyof JSX.IntrinsicElements
  | ComponentType<any> = 'input',
>(
  props: NumberInputProps<InputComponent>,
  ref: ForwardedRef<RefElementType>,
): ReactElement {
  const { disabled, step = 1, type = 'number' } = props;
  const inputRef = useRef<HTMLInputElement>(null);
  const composedRef = useComposedRefs<HTMLInputElement>(
    ref as RefObject<HTMLInputElement>,
    inputRef,
  );
  const onDecrease = useCallback(() => {
    if (!inputRef.current) {
      throw new Error('not found ref input element');
    }

    const { type: inputType, value } = inputRef.current;

    if (inputType === 'number') {
      inputRef.current.stepDown();
      dispatchChangeEvent(inputRef.current, inputRef.current.value);
    } else {
      const inputValue = value || '0';

      const nextValue = (parseFloat(inputValue.replace(/ /g, '')) - step).toString();
      dispatchChangeEvent(inputRef.current, nextValue);
    }
  }, [step]);
  const onIncrease = useCallback(() => {
    if (!inputRef.current) {
      throw new Error('not found ref input element');
    }

    const { type: inputType, value } = inputRef.current;

    if (inputType === 'number') {
      inputRef.current.stepUp();
      dispatchChangeEvent(inputRef.current, inputRef.current.value);
    } else {
      const inputValue = value || '0';

      const nextValue = (parseFloat(inputValue.replace(/ /g, '')) + step).toString();
      dispatchChangeEvent(inputRef.current, nextValue);
    }
  }, [step]);

  const rightButtons = useMemo(() => [
    () => (
      <>
        <ButtonDivider />
        <ActionButton disabled={disabled} onClick={onDecrease}>-</ActionButton>
      </>
    ),
    () => (
      <>
        <ButtonDivider />
        <ActionButton disabled={disabled} onClick={onIncrease}>+</ActionButton>
      </>
    ),
  ], [disabled, onDecrease, onIncrease]);

  return (
    <Wrapper>
      <StyledNumberInput
        {...props}
        disabled={disabled}
        step={step}
        ref={composedRef}
        rightButtons={rightButtons}
        type={type}
      />
    </Wrapper>
  );
}

export const NumberInput = forwardRef(NumberInputInner) as <
  RefElementType,
  InputComponent extends
  | keyof JSX.IntrinsicElements
  | ComponentType<any> = 'input',
>(
  props: NumberInputProps<InputComponent> & {
    ref?: Ref<RefElementType>;
  }
) => ReactElement;
